import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  getCart,
  addToCart,
  updateCartItem,
  batchUpdateCart,
  deleteCartItem,
  clearCompletedCart,
  clearAllCart,
  aiVision,
  aiCategorize,
} from '@recipe/shared';
import { useAuth } from '../contexts/AuthContext';
import { Check, Trash2, Send, Bot, CheckCircle2, Camera, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import AIChatModal from '../components/AIChatModal';
import './Cart.css';

// ── Category classification ──
const CATEGORIES = [
  { key: '蔬菜', emoji: '🥬', keywords: ['菜', '葱', '姜', '蒜', '椒', '瓜', '豆', '笋', '芹', '白菜', '萝卜', '番茄', '西红柿', '土豆', '洋葱', '茄子', '黄瓜', '生菜', '菠菜', '韭菜', '芦笋', '西兰花', '花菜', '蘑菇', '香菇', '木耳', '金针菇', '秋葵', '藕', '山药', '南瓜', '冬瓜', '丝瓜', '苦瓜', '莲藕', '玉米', '芋头', '紫菜', '海带', '豆芽', '油菜', '芥兰', '莴笋', '胡萝卜'] },
  { key: '肉类', emoji: '🥩', keywords: ['肉', '鸡', '鸭', '牛', '猪', '羊', '排骨', '翅', '腿', '胸', '肘', '蹄', '臀', '里脊', '五花', '腊肉', '香肠', '腊肠', '培根', '火腿', '腱子'] },
  { key: '海鲜', emoji: '🦐', keywords: ['鱼', '虾', '蟹', '蚝', '贝', '蛤', '螺', '鱿鱼', '三文鱼', '鲈鱼', '鲫鱼', '鳕鱼', '带鱼', '黄鱼', '墨鱼', '章鱼', '海参', '鲍鱼', '扇贝', '龙虾', '生蚝', '海鲜'] },
  { key: '调料', emoji: '🧂', keywords: ['盐', '酱', '醋', '油', '糖', '酒', '料', '粉', '素', '香料', '花椒', '八角', '桂皮', '辣椒', '胡椒', '五香', '十三香', '咖喱', '孜然', '芥末', '番茄酱', '蚝油', '老抽', '生抽', '豆瓣', '味精', '鸡精', '淀粉', '面粉', '酱油', '麻油', '芝麻', '黄油', '橄榄油', '芝麻油', '豆豉', '腐乳', '蜂蜜'] },
  { key: '乳制品', emoji: '🥛', keywords: ['奶', '牛奶', '酸奶', '芝士', '奶酪', '黄油', '奶油', '炼乳', '奶粉', '酥油', '乳', '起司'] },
  { key: '主食', emoji: '🍞', keywords: ['米', '面', '饭', '粉', '饺', '馒头', '包子', '饼', '面条', '意面', '年糕', '粽', '糯米', '燕麦', '麦片', '面包', '吐司', '馄饨', '河粉', '米粉', '粉丝', '挂面'] },
  { key: '蛋豆', emoji: '🥚', keywords: ['蛋', '鸡蛋', '鸭蛋', '豆腐', '豆干', '豆皮', '腐竹', '豆浆', '千张', '素鸡', '豆花'] },
  { key: '水果', emoji: '🍎', keywords: ['果', '苹果', '香蕉', '橙', '柠檬', '葡萄', '草莓', '蓝莓', '芒果', '西瓜', '梨', '桃', '樱桃', '李子', '柚子', '猕猴桃', '菠萝', '木瓜', '火龙果', '荔枝', '龙眼', '椰', '枣', '干果', '坚果', '核桃', '杏仁', '腰果', '花生'] },
];

function categorizeItem(name) {
  const lower = name.toLowerCase();
  for (const cat of CATEGORIES) {
    for (const kw of cat.keywords) {
      if (lower.includes(kw)) return cat.key;
    }
  }
  return '其他';
}

const Cart = () => {
  const { currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [text, setText] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [isAIChatOpen, setIsAIChatOpen] = useState(false);
  const [isRecognizing, setIsRecognizing] = useState(false);
  const fileInputRef = useRef(null);

  const loadCart = async () => {
    if (!currentUser) return;
    try {
      const data = await getCart();
      setItems(data);
    } catch (error) {
      console.error("Error fetching shopping list: ", error);
      toast.error('加载购物清单失败');
    }
  };

  useEffect(() => {
    loadCart();
  }, [currentUser]);

  // AI Auto-categorize uncategorized items
  useEffect(() => {
    const uncategorizedItems = items.filter(i => !i.category);
    if (uncategorizedItems.length > 0 && currentUser) {
      const categorizeAndSave = async () => {
        try {
          const namesToCategorize = uncategorizedItems.map(i => i.name);
          const categoryMap = await aiCategorize(namesToCategorize);
          
          if (Object.keys(categoryMap).length > 0) {
            const updates = uncategorizedItems.map(item => ({
              id: item.id,
              category: categoryMap[item.name] || categorizeItem(item.name)
            }));
            await batchUpdateCart(updates);
            setItems(prev => prev.map(item => {
              const u = updates.find(x => x.id === item.id);
              return u ? { ...item, category: u.category } : item;
            }));
          }
        } catch (e) {
          console.error('Failed to auto-categorize items:', e);
        }
      };
      
      // Debounce slightly to prevent spam if many items are added rapidly
      const timeoutId = setTimeout(() => categorizeAndSave(), 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [items, currentUser]);

  // ── Build sections ──
  const sections = useMemo(() => {
    const unchecked = items.filter((i) => !i.checked);
    const checked = items.filter((i) => i.checked);

    const groups = {};
    unchecked.forEach((item) => {
      // Use the DB category if available, otherwise fallback
      const cat = item.category || categorizeItem(item.name);
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });

    const result = [];
    for (const cat of CATEGORIES) {
      if (groups[cat.key]?.length > 0) {
        result.push({
          key: cat.key,
          emoji: cat.emoji,
          title: `${cat.emoji} ${cat.key}`,
          data: groups[cat.key],
        });
      }
    }
    if (groups['其他']?.length > 0) {
      result.push({
        key: '其他',
        emoji: '📦',
        title: '📦 其他',
        data: groups['其他'],
      });
    }

    if (checked.length > 0) {
      result.push({
        key: '_checked',
        emoji: '✅',
        title: `✅ 已完成 (${checked.length})`,
        data: checked,
        isChecked: true,
      });
    }

    return result;
  }, [items]);

  const checkedCount = items.filter((i) => i.checked).length;
  const progress = items.length > 0 ? (checkedCount / items.length) * 100 : 0;

  // ── CRUD ──
  const addItem = async () => {
    const trimmed = text.trim();
    if (!trimmed || !currentUser) return;
    
    try {
      const entries = trimmed.split(/[,，\n]+/).map((s) => s.trim()).filter(Boolean);
      const res = await addToCart(entries);
      if (res?.items) {
        setItems(prev => [...prev, ...res.items]);
      } else {
        await loadCart();
      }
      setText('');
    } catch (e) {
      toast.error('添加失败');
    }
  };

  const handleAIAddItems = async (newItems) => {
    if (!currentUser || !newItems || newItems.length === 0) return;
    
    try {
      const formatted = newItems.map(item => typeof item === 'string' ? item : item.name).filter(Boolean);
      const res = await addToCart(formatted);
      if (res?.items) {
        setItems(prev => [...prev, ...res.items]);
      } else {
        await loadCart();
      }
      toast.success(`已添加 ${formatted.length} 项食材`);
    } catch (e) {
      toast.error('批量添加失败');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('图片太大，请上传5MB以下的图片');
      return;
    }

    setIsRecognizing(true);
    const loadingToast = toast.loading('AI 正在识别食材...');

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result.split(',')[1];
        const mimeType = file.type;

        try {
          const recognized = await aiVision(base64Data, mimeType);
          if (recognized && recognized.length > 0) {
            await handleAIAddItems(recognized);
            toast.success(`识别成功！找到了 ${recognized.length} 种食材`);
          } else {
            toast.error('未能识别出任何食材，请换一张图片试试');
          }
        } catch (err) {
          toast.error('识别失败: ' + err.message);
        } finally {
          setIsRecognizing(false);
          toast.dismiss(loadingToast);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setIsRecognizing(false);
      toast.dismiss(loadingToast);
      toast.error('读取图片失败');
    }
  };

  const toggleItem = async (item) => {
    if (!currentUser || editingId) return;
    try {
      const nextChecked = !item.checked;
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, checked: nextChecked } : i));
      await updateCartItem(item.id, { checked: nextChecked });
    } catch (e) {
      toast.error('更新失败');
      await loadCart();
    }
  };

  const deleteItem = async (item, e) => {
    e.stopPropagation();
    if (!currentUser) return;
    try {
      setItems(prev => prev.filter(i => i.id !== item.id));
      await deleteCartItem(item.id);
    } catch (e) {
      toast.error('删除失败');
      await loadCart();
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditingText(item.name);
  };

  const saveEdit = async () => {
    if (!editingId || !currentUser) return;
    const trimmed = editingText.trim();
    if (trimmed) {
      try {
        setItems(prev => prev.map(i => i.id === editingId ? { ...i, name: trimmed } : i));
        await updateCartItem(editingId, { name: trimmed });
      } catch (e) {
        toast.error('更新失败');
        await loadCart();
      }
    }
    setEditingId(null);
    setEditingText('');
  };

  const clearCompleted = async () => {
    if (checkedCount === 0 || !currentUser) return;
    if (window.confirm(`确认删除 ${checkedCount} 个已完成的项目？`)) {
      try {
        setItems(prev => prev.filter(i => !i.checked));
        await clearCompletedCart();
        toast.success('已清理完成项');
      } catch (e) {
        toast.error('清理失败');
        await loadCart();
      }
    }
  };

  const clearAll = async () => {
    if (items.length === 0 || !currentUser) return;
    if (window.confirm('确认清空所有项目？此操作无法撤销。')) {
      try {
        setItems([]);
        await clearAllCart();
        toast.success('已清空清单');
      } catch (e) {
        toast.error('清空失败');
        await loadCart();
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      addItem();
    }
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      setEditingId(null);
    }
  };

  return (
    <div className="container cart-page">
      <div className="cart-header">
        <div>
          <h2>购物清单</h2>
          <p className="cart-subtitle">
            {items.length > 0
              ? `${checkedCount}/${items.length} 已完成`
              : '暂无项目'}
          </p>
        </div>
        <div className="cart-actions">
          <input 
            type="file" 
            accept="image/*" 
            capture="environment" 
            ref={fileInputRef} 
            style={{ display: 'none' }} 
            onChange={handleImageUpload} 
          />
          <button 
            className="ai-helper-btn secondary" 
            onClick={() => fileInputRef.current?.click()}
            disabled={isRecognizing}
            title="拍照识别"
          >
            {isRecognizing ? <Loader2 size={20} className="spin" /> : <Camera size={20} />}
            <span className="ai-btn-text">识别</span>
          </button>
          <button 
            className="ai-helper-btn" 
            onClick={() => setIsAIChatOpen(true)}
            title="小厨 AI 助手"
          >
            <Bot size={20} />
            <span className="ai-btn-text">AI 助手</span>
          </button>
          {checkedCount > 0 && (
            <button className="icon-action-btn success" onClick={clearCompleted} title="清理已完成">
              <CheckCircle2 size={20} />
            </button>
          )}
          {items.length > 0 && (
            <button className="icon-action-btn danger" onClick={clearAll} title="清空全部">
              <Trash2 size={20} />
            </button>
          )}
        </div>
      </div>

      {items.length > 0 && (
        <div className="progress-container">
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      <div className="cart-content">
        {items.length === 0 ? (
          <div className="empty-cart">
            <div className="empty-icon">🛒</div>
            <h3>清单是空的</h3>
            <p>在下方输入食材，或使用 AI 助手帮你生成清单</p>
          </div>
        ) : (
          <div className="cart-sections">
            {sections.map((section) => (
              <div key={section.key} className={`cart-section ${section.isChecked ? 'checked-section' : ''}`}>
                <div className="section-header">
                  <h4 className="section-title">{section.title}</h4>
                  <span className="section-count">{section.data.length}</span>
                </div>
                <div className="section-items">
                  {section.data.map((item) => (
                    <div 
                      key={item.id} 
                      className={`cart-item ${item.checked ? 'checked' : ''}`}
                      onClick={() => toggleItem(item)}
                      onDoubleClick={() => startEdit(item)}
                    >
                      <div className="checkbox">
                        {item.checked && <Check size={14} strokeWidth={3} />}
                      </div>
                      
                      {editingId === item.id ? (
                        <input
                          type="text"
                          className="edit-input"
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          onBlur={saveEdit}
                          onKeyDown={handleEditKeyDown}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <span className="item-name">{item.name}</span>
                      )}
                      
                      <button 
                        className="delete-btn" 
                        onClick={(e) => deleteItem(item, e)}
                        title="删除"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="cart-input-area">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="输入要买的食材（可用逗号分隔）..."
          className="add-input"
        />
        <button 
          className="send-btn" 
          onClick={addItem}
          disabled={!text.trim()}
        >
          <Send size={18} />
        </button>
      </div>

      <AIChatModal 
        isOpen={isAIChatOpen} 
        onClose={() => setIsAIChatOpen(false)} 
        cartItems={items}
        onAddItems={handleAIAddItems}
      />
    </div>
  );
};

export default Cart;
